import { useCallback, useEffect, useMemo, useState } from "react";
import { listNiches, runScan, getScan, listScans, clearNiches } from "@/lib/api";
import NicheCard from "@/components/NicheCard";
import NicheAnalysisModal from "@/components/NicheAnalysisModal";
import ScanRunningModal from "@/components/ScanRunningModal";
import EarningsDistribution from "@/components/EarningsDistribution";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

const SORTS = [
  { key: "score", label: "Score" },
  { key: "recent", label: "Recent" },
  { key: "monetization", label: "Monetization" },
];

export default function Dashboard({ bookmarkedOnly = false }) {
  const { user } = useAuth();
  const [niches, setNiches] = useState([]);
  const [scans, setScans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);
  const [sort, setSort] = useState("score");
  const [analyzeNiche, setAnalyzeNiche] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [n, s] = await Promise.all([
        listNiches(bookmarkedOnly ? true : undefined),
        listScans(),
      ]);
      setNiches(n);
      setScans(s);
    } catch (e) {
      toast.error("Failed to load niches");
    } finally {
      setLoading(false);
    }
  }, [bookmarkedOnly]);

  useEffect(() => { load(); }, [load]);

  const handleScan = async (replace = false) => {
    if (replace && niches.length > 0) {
      const ok = window.confirm(
        `Clear ${niches.filter((n) => !n.bookmarked).length} non-saved niche${
          niches.length === 1 ? "" : "s"
        } and run a fresh scan?\n\nBookmarked niches will be preserved.`,
      );
      if (!ok) return;
    }
    setScanning(true);
    try {
      if (replace) {
        const res = await clearNiches(true);
        if (res.deleted > 0) {
          toast.success(`Cleared ${res.deleted} previous niche${res.deleted === 1 ? "" : "s"}`);
        }
        // Refresh state before scan modal shows
        setNiches((curr) => curr.filter((n) => n.bookmarked));
      }
      const { scan_id } = await runScan();
      const start = Date.now();
      while (Date.now() - start < 1000 * 60 * 5) {
        await new Promise((r) => setTimeout(r, 2500));
        const s = await getScan(scan_id);
        if (s.status === "complete") {
          await load();
          const usedClaude = s.analyzer === "claude-sonnet-4-5";
          toast.success(
            usedClaude
              ? `Scan complete — ${s.niche_count} niches via Claude Sonnet 4.5`
              : `Scan complete — ${s.niche_count} niches (heuristic fallback)`,
          );
          return;
        }
        if (s.status === "failed") {
          toast.error(s.error || "Scan failed");
          return;
        }
      }
      toast.error("Scan timed out after 5 minutes");
    } catch (e) {
      const msg = e?.response?.data?.detail || "Scan failed";
      toast.error(typeof msg === "string" ? msg : "Scan failed");
    } finally {
      setScanning(false);
    }
  };

  const handleClearOnly = async () => {
    const nonSaved = niches.filter((n) => !n.bookmarked).length;
    if (nonSaved === 0) {
      toast.info("Nothing to clear (only bookmarked niches remain)");
      return;
    }
    const ok = window.confirm(
      `Clear ${nonSaved} non-saved niche${nonSaved === 1 ? "" : "s"}?\n\nBookmarked niches will be preserved.`,
    );
    if (!ok) return;
    try {
      const res = await clearNiches(true);
      await load();
      toast.success(`Cleared ${res.deleted} niche${res.deleted === 1 ? "" : "s"}`);
    } catch {
      toast.error("Could not clear niches");
    }
  };

  const sorted = useMemo(() => {
    const arr = [...niches];
    if (sort === "score") {
      arr.sort((a, b) => (b.scores?.composite_score || 0) - (a.scores?.composite_score || 0));
    } else if (sort === "recent") {
      arr.sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));
    } else if (sort === "monetization") {
      arr.sort((a, b) => (b.scores?.monetization_potential || 0) - (a.scores?.monetization_potential || 0));
    }
    return arr;
  }, [niches, sort]);

  const lastScan = scans[0];

  return (
    <div data-testid="dashboard-page" className="px-6 lg:px-10 py-8 max-w-[1500px] mx-auto">
      {/* Header bar */}
      <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-10">
        <div>
          <div className="font-mono text-xs uppercase tracking-[0.3em] text-yellow-400 mb-3">
            // OPERATOR: {user?.name?.split(" ")[0] || "Anonymous"}
          </div>
          <h1 className="font-display text-4xl sm:text-5xl font-black text-white leading-none mb-3">
            {bookmarkedOnly ? "Saved niches" : "Niche opportunities"}
          </h1>
          <p className="text-zinc-400 max-w-xl text-sm">
            {bookmarkedOnly
              ? "Your bookmarked niches. Pinned for when you're ready to ship."
              : "Channels under 25k subs with breakout videos, clustered into ranked opportunities and scored across five pillars."}
          </p>
        </div>

        <div className="flex items-center gap-3 flex-wrap">
          <div className="font-mono text-[11px] text-zinc-500 hidden lg:block">
            {lastScan ? (
              <>last_scan ::= {new Date(lastScan.created_at).toLocaleString()}</>
            ) : (
              <>last_scan ::= none</>
            )}
          </div>
          {!bookmarkedOnly && niches.some((n) => !n.bookmarked) && (
            <button
              data-testid="clear-niches-btn"
              onClick={handleClearOnly}
              disabled={scanning}
              className="font-mono text-xs uppercase tracking-wider px-4 py-3 border border-zinc-700 text-zinc-300 hover:border-red-500/60 hover:text-red-400 rounded-sm transition-colors disabled:opacity-60"
              title="Delete current niches (bookmarked are kept)"
            >
              ✕ Clear results
            </button>
          )}
          <button
            data-testid="run-scan-btn"
            onClick={() => handleScan(false)}
            disabled={scanning}
            className="bg-yellow-400 text-yellow-950 font-bold px-6 py-3 rounded-sm uppercase tracking-wide text-sm hover:bg-yellow-300 transition-colors disabled:opacity-60"
          >
            {scanning ? "Scanning…" : "▸ Run scan now"}
          </button>
          {!bookmarkedOnly && niches.some((n) => !n.bookmarked) && (
            <button
              data-testid="clear-and-rescan-btn"
              onClick={() => handleScan(true)}
              disabled={scanning}
              className="font-mono text-xs uppercase tracking-wider px-4 py-3 border border-yellow-400/60 text-yellow-400 hover:bg-yellow-400/10 rounded-sm transition-colors disabled:opacity-60"
              title="Clear current niches and run a fresh scan"
            >
              ⟲ Clear & rescan
            </button>
          )}
        </div>
      </div>

      {/* Stats strip */}
      {!bookmarkedOnly && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-zinc-800 border border-zinc-800 mb-10 rounded-sm overflow-hidden">
          {[
            ["Niches tracked", niches.length],
            ["Scans run", scans.length],
            ["Avg score", niches.length
              ? Math.round(
                  niches.reduce((s, n) => s + (n.scores?.composite_score || 0), 0) / niches.length
                )
              : "—"],
            ["Breakout channels", new Set(niches.flatMap((n) => (n.videos || []).map((v) => v.channel_id))).size],
          ].map(([label, val]) => (
            <div key={label} className="bg-black p-5">
              <div className="font-mono text-[11px] uppercase tracking-[0.25em] text-zinc-500 mb-2">
                {label}
              </div>
              <div className="font-mono font-bold text-3xl text-yellow-400 tracking-tight">
                {val}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Earnings distribution chart — only on the main niches dashboard, not Saved */}
      {!bookmarkedOnly && sorted.length > 1 && (
        <EarningsDistribution niches={sorted} />
      )}

      {/* Sort bar */}
      {niches.length > 0 && (
        <div className="flex items-center gap-2 mb-6">
          <span className="font-mono text-[11px] uppercase tracking-[0.25em] text-zinc-500 mr-2">
            sort by
          </span>
          {SORTS.map((s) => (
            <button
              key={s.key}
              data-testid={`sort-${s.key}`}
              onClick={() => setSort(s.key)}
              className={`font-mono text-xs uppercase tracking-wider px-3 py-1.5 rounded-sm border transition-colors ${
                sort === s.key
                  ? "border-yellow-400 text-yellow-400"
                  : "border-zinc-800 text-zinc-500 hover:text-white hover:border-zinc-700"
              }`}
            >
              {s.label}
            </button>
          ))}
        </div>
      )}

      {/* Grid */}
      {loading ? (
        <div className="font-mono text-sm text-zinc-500">
          [recon] loading targets<span className="blink">_</span>
        </div>
      ) : sorted.length === 0 ? (
        <EmptyState onScan={() => handleScan(false)} bookmarkedOnly={bookmarkedOnly} />
      ) : (
        <div
          data-testid="niche-grid"
          className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4"
        >
          {sorted.map((n) => (
            <NicheCard
              key={n.niche_id}
              niche={n}
              onAnalyze={bookmarkedOnly ? setAnalyzeNiche : undefined}
            />
          ))}
        </div>
      )}

      <ScanRunningModal open={scanning} />
      <NicheAnalysisModal
        niche={analyzeNiche}
        open={Boolean(analyzeNiche)}
        onClose={() => setAnalyzeNiche(null)}
        onAnalysisComplete={(nicheId, analysis) => {
          setNiches((curr) =>
            curr.map((n) =>
              n.niche_id === nicheId ? { ...n, analysis } : n
            )
          );
        }}
      />
    </div>
  );
}

function EmptyState({ onScan, bookmarkedOnly }) {
  return (
    <div className="border border-dashed border-zinc-800 rounded-sm p-12 text-center bg-zinc-950">
      <div className="font-mono text-yellow-400 text-xs uppercase tracking-[0.3em] mb-4">
        [ AWAITING RECON ]
      </div>
      <h3 className="font-display text-2xl font-bold text-white mb-3">
        {bookmarkedOnly ? "No saved niches yet" : "No niches scanned yet"}
      </h3>
      <p className="text-zinc-400 max-w-md mx-auto text-sm mb-6">
        {bookmarkedOnly
          ? "Bookmark niches from the dashboard to pin them here."
          : "Hit 'Run scan now' to sweep YouTube for breakout videos under 25k subs and let the model surface ranked niches."}
      </p>
      {!bookmarkedOnly && (
        <button
          data-testid="empty-state-scan-btn"
          onClick={onScan}
          className="bg-yellow-400 text-yellow-950 font-bold px-6 py-3 rounded-sm uppercase tracking-wide text-sm hover:bg-yellow-300 transition-colors"
        >
          ▸ Run first scan
        </button>
      )}
    </div>
  );
}
